/**
 * Wrist bar for hand tracking, worn on the BACK of the wrist:
 *
 *        ( ‹ )  [ ≡ Menu ]  ( › )
 *           [ ↶  Turn around ]
 *
 * It appears when you look at the back of your hand like a watch. The palm
 * side is left alone on purpose: palm-toward-face is where Meta's own system
 * menu / Meta buttons live, and sharing that spot made presses land on both.
 * Press with the other hand — poke it, or point and pinch.
 */
import React, { useMemo, useState } from 'react';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { vrColors as colors, noRaycast, roundedRect } from '../vrUi';

export type WristPart = 'left' | 'menu' | 'right' | 'around';

// Layout in metres, in the bar's own plane (+y up, +z toward your eyes).
const R = .022;                 // round turn keys
const MW = .096, MH = .044;     // Menu pill
const GAP = .008;
const X_TURN = MW / 2 + GAP + R;
const Y_TOP = .019;             // main row
const AW = .14, AH = .03;       // Turn around pill
const Y_AROUND = Y_TOP - MH / 2 - GAP - AH / 2;
const PAD = .01;
const BAR_W = 2 * (X_TURN + R) + 2 * PAD;
const BAR_H = (Y_TOP + MH / 2) - (Y_AROUND - AH / 2) + 2 * PAD;
const BAR_Y = (Y_TOP + MH / 2 + Y_AROUND - AH / 2) / 2;
const SLOP = .005;              // fingertips are fat: count near-misses

/** Which button is at (x, y) in the bar's own plane (metres), for finger pokes. */
export function wristBarPartAt(x: number, y: number): WristPart | null {
  if (Math.abs(y - Y_TOP) <= MH / 2 + SLOP) {
    if (Math.abs(x) <= MW / 2 + GAP / 2) return 'menu';
    if (Math.abs(x) <= X_TURN + R + SLOP) return x < 0 ? 'left' : 'right';
    return null;
  }
  if (Math.abs(y - Y_AROUND) <= AH / 2 + SLOP && Math.abs(x) <= AW / 2 + SLOP) return 'around';
  return null;
}

const FILL = '#1a2a3a', FILL_MAIN = '#17463f', HOVER = '#5ff0e3', DARK = '#04211e';
const flat = (color: string, opacity = 1) => <meshBasicMaterial color={color} transparent={opacity < 1} opacity={opacity} toneMapped={false} side={THREE.DoubleSide} />;

function Key({ id, x, y, poked, label, onPress, shape, rim, children }: {
  id: WristPart; x: number; y: number; poked: boolean; label?: string; onPress: () => void;
  shape: React.ReactNode; rim?: React.ReactNode; children: (fg: string, hover: boolean) => React.ReactNode;
}) {
  const [rayHover, setHover] = useState(false);
  const hover = rayHover || poked;
  return <group position={[x, y, 0]} name={`vr-wrist-${id}`} scale={hover ? 1.06 : 1}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }}
    onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); onPress(); }}>
    {rim}
    <mesh position={[0, 0, .001]}>{shape}{flat(hover ? HOVER : id === 'menu' ? FILL_MAIN : FILL)}</mesh>
    <group position={[0, 0, .003]}>{children(hover ? DARK : colors.text, hover)}</group>
    {/* Name tag above the round keys, which have no words of their own. */}
    {hover && label && <group position={[0, R + .03, .004]}>
      <mesh raycast={noRaycast}><shapeGeometry args={[roundedRect(label.length * .0098 + .022, .024, .012)]} />{flat(colors.panel, .96)}</mesh>
      <Text raycast={noRaycast} position={[0, 0, .001]} fontSize={.0125} color={colors.teal}>{label}</Text>
    </group>}
  </group>;
}

function Chevron({ dir, color }: { dir: 1 | -1; color: string }) {
  return <group scale={[dir, 1, 1]} position={[dir * .002, 0, 0]}>
    {[1, -1].map((s) => <mesh key={s} raycast={noRaycast} position={[0, s * .0048, 0]} rotation={[0, 0, -s * Math.PI / 4]}>
      <planeGeometry args={[.0165, .0036]} />{flat(color)}
    </mesh>)}
  </group>;
}

function MenuGlyph({ color }: { color: string }) {
  return <group>{[1, 0, -1].map((i) => <mesh key={i} raycast={noRaycast} position={[0, i * .0058, 0]}>
    <planeGeometry args={[.0155, .0026]} />{flat(color)}
  </mesh>)}</group>;
}

/** U-turn: a half ring with an arrowhead on its open end. */
function AroundGlyph({ color }: { color: string }) {
  const head = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-.0045, 0); s.lineTo(.0045, 0); s.lineTo(0, -.0058); s.closePath();
    return s;
  }, []);
  return <group>
    <mesh raycast={noRaycast}><ringGeometry args={[.0042, .0068, 20, 1, 0, Math.PI]} />{flat(color)}</mesh>
    <mesh raycast={noRaycast} position={[-.0055, .0004, 0]}><shapeGeometry args={[head]} />{flat(color)}</mesh>
  </group>;
}

export default function VRWristBar({ onMenu, onTurnLeft, onTurnRight, onTurnAround, poke = null }: {
  onMenu: () => void; onTurnLeft: () => void; onTurnRight: () => void; onTurnAround: () => void;
  /** Part under the other hand's fingertip (finger poke), highlighted like a hover. */
  poke?: string | null;
}) {
  const geo = useMemo(() => ({
    bar: roundedRect(BAR_W, BAR_H, .024),
    rim: roundedRect(BAR_W + .004, BAR_H + .004, .026),
    pill: roundedRect(MW, MH, MH / 2),
    around: roundedRect(AW, AH, AH / 2),
  }), []);
  const round = <circleGeometry args={[R, 36]} />;
  return <group name="vr-wrist-dial">
    {/* Glass card with a thin teal rim, like the rest of the VR UI. */}
    <mesh raycast={noRaycast} position={[0, BAR_Y, -.004]}><shapeGeometry args={[geo.rim]} />{flat(colors.teal, .35)}</mesh>
    <mesh raycast={noRaycast} position={[0, BAR_Y, -.003]}><shapeGeometry args={[geo.bar]} />{flat(colors.panel, .92)}</mesh>

    <Key id="left" x={-X_TURN} y={Y_TOP} poked={poke === 'left'} label="Turn left" onPress={onTurnLeft} shape={round}>
      {(fg) => <Chevron dir={-1} color={fg} />}
    </Key>
    <Key id="menu" x={0} y={Y_TOP} poked={poke === 'menu'} onPress={onMenu} shape={<shapeGeometry args={[geo.pill]} />}>
      {(fg) => <>
        <group position={[-.03, 0, 0]}><MenuGlyph color={fg} /></group>
        <Text raycast={noRaycast} position={[.011, -.0005, 0]} fontSize={.0175} color={fg}>Menu</Text>
      </>}
    </Key>
    <Key id="right" x={X_TURN} y={Y_TOP} poked={poke === 'right'} label="Turn right" onPress={onTurnRight} shape={round}>
      {(fg) => <Chevron dir={1} color={fg} />}
    </Key>
    <Key id="around" x={0} y={Y_AROUND} poked={poke === 'around'} onPress={onTurnAround} shape={<shapeGeometry args={[geo.around]} />}>
      {(fg, hover) => <>
        <group position={[-.042, .001, 0]}><AroundGlyph color={fg} /></group>
        <Text raycast={noRaycast} position={[.008, -.0004, 0]} fontSize={.0135} color={hover ? fg : colors.muted}>Turn around</Text>
      </>}
    </Key>
  </group>;
}
